
/*
 * DRAG SHADE — Original Artwork Integration
 *
 * Uses SB Product Designer's existing upload button.
 * Saves the untouched original through Upload Native v3.
 * Adds the original file URL directly to the personalized
 * product's Shopify line-item properties.
 *
 * No second customer-facing upload button.
 */

(() => {
  if (window.dragShadeOriginalUploadInstalled) return;
  window.dragShadeOriginalUploadInstalled = true;

  const originalFetch = window.fetch;

  let uploadPending = false;
  let uploadFailed = false;
  let uploadSequence = 0;
  let originalArtworkURL = null;

  // Capture the original file selected in SB Designer.
  document.addEventListener('change', async (event) => {
    if (!event.target.matches(
      'input.custom-file-input[type="file"]'
    )) return;

    const file = event.target.files?.[0];
    if (!file) return;

    const sequence = ++uploadSequence;

    uploadPending = true;
    uploadFailed = false;
    originalArtworkURL = null;

    console.log('[Drag Shade] Saving original:', file.name);

    try {
      if (!window.UploadNative?.upload) {
        throw new Error('Upload Native SDK is unavailable.');
      }

      const cartResponse = await originalFetch.call(window, '/cart.js');

      if (!cartResponse.ok) {
        throw new Error('Could not retrieve Shopify cart.');
      }

      const cart = await cartResponse.json();

      const ref = await window.UploadNative.upload(file, {
        base: '/apps/upload-app',
        reference: cart.token,
        allowed: ['image/*', 'application/pdf']
      });

      if (!ref?.fileUrl) {
        throw new Error('Upload returned no file URL.');
      }

      // Ignore an older upload if the image was replaced.
      if (sequence !== uploadSequence) return;

      originalArtworkURL = ref.fileUrl;
      uploadPending = false;
      uploadFailed = false;

      console.log(
        '[Drag Shade] Original saved:',
        originalArtworkURL
      );

    } catch (error) {
      if (sequence !== uploadSequence) return;

      uploadPending = false;
      uploadFailed = true;
      originalArtworkURL = null;

      console.error(
        '[Drag Shade] Original upload failed:',
        error
      );

      alert(
        'Your original artwork could not be saved. ' +
        'Please select your image again before adding to cart.'
      );
    }
  }, true);

  // Identify Shopify's cart/add endpoint.
  function isCartAddRequest(input) {
    try {
      const url = new URL(
        typeof input === 'string' ? input : input.url,
        window.location.origin
      );

      return (
        url.origin === window.location.origin &&
        /^\/cart\/add(?:\.js)?\/?$/.test(url.pathname)
      );
    } catch {
      return false;
    }
  }

  // Add the original artwork URL to SB's existing
  // personalization properties without replacing them.
  window.fetch = async function(input, init) {
    if (!isCartAddRequest(input)) {
      return originalFetch.apply(this, arguments);
    }

    // Work from a Request so both fetch(url, options)
    // and fetch(Request) are supported.
    const request = new Request(input, init);

    if (request.method.toUpperCase() !== 'POST') {
      return originalFetch.apply(this, arguments);
    }

    let payload;

    try {
      payload = await request.clone().json();
    } catch {
      // Leave unrelated or non-JSON cart requests untouched.
      return originalFetch.apply(this, arguments);
    }

    const items = Array.isArray(payload.items)
      ? payload.items
      : null;

    // Only modify SB's personalized product submissions.
    const personalizedItems = items?.filter(item =>
      item?.properties?.pd_personalization_id ||
      item?.properties?._personalization_id
    );

    if (!personalizedItems?.length) {
      return originalFetch.apply(this, arguments);
    }

    // Prevent a personalized product being submitted without
    // its full-resolution original artwork.
    if (uploadPending || uploadFailed || !originalArtworkURL) {
      const message = uploadPending
        ? 'Your original artwork is still uploading. Please wait a moment and click Add to Cart again.'
        : 'Your original artwork is not ready. Please upload your image again before adding to cart.';

      console.error('[Drag Shade] Cart add blocked:', message);
      alert(message);

      // Reject rather than silently submit an incomplete order.
      return Promise.reject(new Error(message));
    }

    // Preserve every property supplied by SB Designer.
    // Only add our new property to personalized items.
    payload.items = items.map(item => {
      if (
        !item?.properties?.pd_personalization_id &&
        !item?.properties?._personalization_id
      ) {
        return item;
      }

      return {
        ...item,
        properties: {
          ...item.properties,
          'Original Artwork': originalArtworkURL
        }
      };
    });

    console.log(
      '[Drag Shade] Original artwork added to line item:',
      originalArtworkURL
    );

    const headers = new Headers(request.headers);
    headers.delete('content-length');

    const modifiedRequest = new Request(request, {
      headers,
      body: JSON.stringify(payload)
    });

    return originalFetch.call(this, modifiedRequest);
  };

  // Keep the existing button protection as an additional safeguard.
  function blockIfNecessary(event) {
    if (!uploadPending && !uploadFailed) return false;

    event.preventDefault();
    event.stopImmediatePropagation();

    alert(uploadPending
      ? 'Your original artwork is still uploading. Please wait a moment.'
      : 'Your original artwork was not saved. Please upload your image again.'
    );

    return true;
  }

  document.addEventListener('click', event => {
    const button = event.target.closest(
      'button[name="add"], ' +
      'button[type="submit"][name="add"], ' +
      '.product-form__submit'
    );

    if (button) blockIfNecessary(event);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;

    if (form.matches(
      'form[action*="/cart/add"], form[id^="product-form"]'
    )) {
      blockIfNecessary(event);
    }
  }, true);

  console.log(
    '[Drag Shade] Original artwork integration ready.'
  );
})();
